import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { LoadingStatuses } from '@/common/constants';
import { useAcceptDpaAmendment } from '@/hooks/query/osint.hooks';
import { useToast } from '@/hooks/useToast';
import DataCollectingIcon from '@/images/icons/data-collecting.svg';
import { AppButton } from '@/ui/buttons';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { AxiosError } from 'axios';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 649px;
  border-radius: var(--semi-medium-border-radius);

  .p-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--medium-big-padding) var(--semi-big-padding);
  }

  .p-dialog-header {
    padding-bottom: 0;
  }
`;

const StyledTitle = styled.h2`
  font-size: var(--modal-heading-font-size);
  font-weight: 600;
  line-height: var(--default-line-height);
  margin: 0;
`;

const StyledBody = styled.p`
  font-size: var(--medium-font-size);
  line-height: var(--default-line-height);
  margin: var(--default-padding-small) 0 0;
`;

const StyledSubtitle = styled.p`
  font-size: var(--medium-font-size);
  line-height: var(--default-line-height);
  margin: var(--medium-padding) 0 0;
`;

const StyledToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  margin-top: var(--default-padding);
`;

const StyledToggleLabel = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
`;

const StyledButtonRow = styled.div`
  display: flex;
  gap: var(--default-padding-small);
  margin-top: var(--default-padding);
`;

const StyledAcceptButton = styled(AppButton)`
  &.p-button {
    background: var(--red-dark);
    border-color: var(--red-dark);
    border-radius: var(--default-border-radius);
    color: var(--white-main);
    min-width: 197px;
    height: var(--default-input-height);

    &:hover:not(:disabled) {
      background: var(--red-dark-hover);
      border-color: var(--red-dark-hover);
    }

    &:disabled {
      opacity: 0.5;
    }
  }
`;

const StyledDownloadButton = styled(AppButton)`
  &.p-button.p-button-outlined {
    border-radius: var(--default-border-radius);
    border-color: var(--purple-dark);
    color: var(--purple-dark);
    min-width: 215px;
    height: var(--default-input-height);

    &:hover {
      background: var(--black-opacity-subtle);
    }
  }
`;

const StyledConfirmationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--default-padding) 0 var(--semi-big-padding);
`;

const StyledConfirmationIcon = styled.img`
  width: 93px;
  height: 93px;
  margin-bottom: var(--semi-medium-padding);
`;

const StyledConfirmationTitle = styled.h2`
  font-size: var(--modal-heading-font-size);
  font-weight: 600;
  line-height: var(--default-line-height);
  margin: 0 0 var(--semi-medium-padding);
  max-width: 576px;
`;

const StyledConfirmationBody = styled.p`
  font-size: var(--medium-font-size);
  line-height: var(--default-line-height);
  margin: 0;
  max-width: 576px;

  &:not(:first-of-type) {
    margin-top: var(--medium-padding);
  }
`;

const StyledBackButton = styled(AppButton)`
  &.p-button {
    background: var(--red-dark);
    border-color: var(--red-dark);
    border-radius: var(--default-border-radius);
    color: var(--white-main);
    height: var(--default-input-height);
    margin-top: var(--semi-big-padding);

    &:hover {
      background: var(--red-dark-hover);
      border-color: var(--red-dark-hover);
    }
  }
`;

type DpaAcceptanceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAccepted: () => void;
  accountId: string;
};

export const DpaAcceptanceModal: React.FC<DpaAcceptanceModalProps> = ({
  isOpen,
  onClose,
  onAccepted,
  accountId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { acceptDpa, isLoading } = useAcceptDpaAmendment(accountId);
  const [toggleOn, setToggleOn] = useState(false);
  const [step, setStep] = useState<'accept' | 'collecting'>('accept');

  useEffect(() => {
    if (!isOpen) return;
    setToggleOn(false);
    setStep('accept');
  }, [isOpen]);

  const isAcceptEnabled = toggleOn;

  const handleDownload = async () => {
    try {
      const { downloadUrl } = await client.accountOsint.getDpaDocument(
        accountId,
      );
      window.open(downloadUrl, '_blank');
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleAccept = async () => {
    try {
      await acceptDpa();
      toast?.success(t('toast.success'), t('osint.dpa.accepted.success'));
      setStep('collecting');
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleBackToPlanner = () => {
    onAccepted();
    onClose();
  };

  return (
    <StyledDialog
      visible={isOpen}
      onHide={onClose}
      modal
      draggable={false}
      resizable={false}
      className="v2"
      closable={step === 'accept'}
      pt={{ root: { dir: document.documentElement.dir || 'ltr' } }}
    >
      {step === 'accept' && (
        <>
          <StyledTitle>{t('osint.dpa.dialog.title')}</StyledTitle>
          <StyledBody>{t('osint.dpa.dialog.body')}</StyledBody>
          <StyledSubtitle>{t('osint.dpa.dialog.reviewBefore')}</StyledSubtitle>

          <StyledToggleRow>
            <AppSwitch
              checked={toggleOn}
              onChange={(e) => setToggleOn(e.value)}
            />
            <StyledToggleLabel>
              {t('osint.dpa.dialog.toggleLabel')}
            </StyledToggleLabel>
          </StyledToggleRow>

          <StyledButtonRow>
            <span
              className="accept-dpa-tooltip-target"
              data-pr-tooltip={
                !isAcceptEnabled ? t('osint.dpa.dialog.tooltip') : undefined
              }
              data-pr-position="bottom"
            >
              <StyledAcceptButton
                label={t('osint.dpa.dialog.accept')}
                isDisabled={!isAcceptEnabled}
                state={
                  isLoading ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
                onClick={handleAccept}
              />
            </span>
            <StyledDownloadButton
              label={t('osint.dpa.dialog.download')}
              type="outlined"
              severity="secondary"
              state={LoadingStatuses.IDLE}
              onClick={handleDownload}
            />
          </StyledButtonRow>
          {!isAcceptEnabled && (
            <Tooltip
              target=".accept-dpa-tooltip-target"
              position="bottom"
              style={{ maxWidth: '300px' }}
              pt={{ root: { dir: 'auto' } }}
            />
          )}
        </>
      )}

      {step === 'collecting' && (
        <StyledConfirmationContent>
          <StyledConfirmationIcon src={DataCollectingIcon} alt="" />
          <StyledConfirmationTitle>
            {t('osint.dpa.dialog.collecting.title')}
          </StyledConfirmationTitle>
          <StyledConfirmationBody>
            {t('osint.dpa.dialog.collecting.body1')}
          </StyledConfirmationBody>
          <StyledConfirmationBody>
            {t('osint.dpa.dialog.collecting.body2')}
          </StyledConfirmationBody>
          <StyledBackButton
            label={t('osint.dpa.dialog.collecting.backToPlanner')}
            state={LoadingStatuses.IDLE}
            onClick={handleBackToPlanner}
          />
        </StyledConfirmationContent>
      )}
    </StyledDialog>
  );
};
